/**
 * Engagement Message Service
 * Generates contextual messages to drive user engagement based on milestones and financial progress
 */

import { formatCurrency } from '../utilities/general-utils';

export class EngagementMessageService {
    
    /**
     * Generate engagement messages based on user's financial state and milestones
     */
    static generateEngagementMessages(userState, milestones = []) {
        const messages = [];

        // Debt payoff celebrations
        if (userState.recentPayoffs?.length > 0) {
            userState.recentPayoffs.forEach(payoff => {
                messages.push(this.createDebtPayoffMessage(payoff));
            });
        }

        // Budget milestone warnings/celebrations
        if (userState.budgetState) {
            const budgetMessage = this.createBudgetStateMessage(userState.budgetState);
            if (budgetMessage) messages.push(budgetMessage);
        }

        // Progress milestone celebrations
        if (userState.snowballProgress) {
            const progressMessage = this.createProgressMessage(userState.snowballProgress);
            if (progressMessage) messages.push(progressMessage);
        }

        // Streak and consistency messages
        if (userState.streakData) {
            const streakMessage = this.createStreakMessage(userState.streakData);
            if (streakMessage) messages.push(streakMessage);
        }

        return messages;
    }

    /**
     * Create debt payoff celebration messages
     */
    static createDebtPayoffMessage(payoff) {
        const celebrations = [
            "🎉 Incredible! You just paid off your {accountName}!",
            "🔥 {accountName} is GONE! You're {amount} closer to financial freedom!",
            "💪 Another one bites the dust! {accountName} has been eliminated!",
            "🚀 {accountName} paid off! You're crushing your debt snowball!"
        ];

        const celebration = celebrations[Math.floor(Math.random() * celebrations.length)];
        
        return {
            type: 'celebration',
            priority: 'high',
            category: 'debt_payoff',
            title: 'Debt Eliminated! 🎉',
            message: celebration
                .replace('{accountName}', payoff.accountName)
                .replace('{amount}', formatCurrency(payoff.amount)),
            data: {
                accountId: payoff.accountId,
                amount: payoff.amount,
                remainingDebts: payoff.remainingDebts,
                totalSaved: payoff.interestSaved
            },
            displayTime: 8000, // Longer display for major wins
            actions: [
                {
                    label: 'View Your Progress',
                    action: 'navigate',
                    target: '/tools/debt-snowball'
                },
                {
                    label: 'Share Achievement',
                    action: 'share',
                    data: payoff
                }
            ]
        };
    }

    /**
     * Create budget state messages (warnings and celebrations)
     */
    static createBudgetStateMessage(budgetState) {
        if (budgetState.type === 'minimum_reached') {
            return {
                type: 'warning',
                priority: 'urgent',
                category: 'budget_warning',
                title: '⚠️ Budget Alert',
                message: `You've reached your minimum balance of ${formatCurrency(budgetState.minimumAmount)}. Consider reviewing your spending.`,
                data: budgetState,
                displayTime: 10000,
                actions: [
                    {
                        label: 'Review Budget',
                        action: 'navigate',
                        target: '/dashboard'
                    }
                ]
            };
        } else if (budgetState.type === 'positive_flow') {
            return {
                type: 'celebration',
                priority: 'medium',
                category: 'budget_success',
                title: '📈 Budget Success!',
                message: `Great job! You have ${formatCurrency(budgetState.surplus)} positive cash flow this month!`,
                data: budgetState,
                displayTime: 6000,
                actions: [
                    {
                        label: 'Apply to Debt',
                        action: 'navigate',
                        target: '/tools/debt-snowball'
                    }
                ]
            };
        }

        return null;
    }

    /**
     * Create snowball progress messages
     */
    static createProgressMessage(progress) {
        if (progress.milestone === 'halfway') {
            return {
                type: 'milestone',
                priority: 'high',
                category: 'progress',
                title: '🎯 Halfway There!',
                message: `Amazing progress! You're halfway to being debt-free. ${formatCurrency(progress.remaining)} left to go!`,
                data: progress,
                displayTime: 7000,
                actions: [
                    {
                        label: 'View Schedule',
                        action: 'navigate',
                        target: '/tools/debt-snowball/schedule'
                    }
                ]
            };
        } else if (progress.milestone === 'month_ahead') {
            return {
                type: 'celebration',
                priority: 'medium',
                category: 'progress',
                title: '⚡ Ahead of Schedule!',
                message: `You're crushing it! Your extra payments put you ${progress.monthsAhead} months ahead of schedule!`,
                data: progress,
                displayTime: 6000
            };
        }

        return null;
    }

    /**
     * Create streak and consistency messages
     */
    static createStreakMessage(streakData) {
        const streakMilestones = [7, 14, 30, 60, 90, 180, 365];
        
        if (streakMilestones.includes(streakData.days)) {
            const streakMessages = {
                7: "🔥 One week streak! You're building great habits!",
                14: "💪 Two weeks of consistent payments! Keep it up!",
                30: "🚀 One month streak! You're on fire!",
                60: "🏆 Two months of consistency! Incredible dedication!",
                90: "🎖️ Three months strong! You're a debt-fighting machine!",
                180: "⭐ Six months of consistency! Financial discipline achieved!",
                365: "🏅 One full year! You're a financial champion!"
            };

            return {
                type: 'streak',
                priority: 'medium',
                category: 'consistency',
                title: 'Consistency Champion! 🔥',
                message: streakMessages[streakData.days],
                data: streakData,
                displayTime: 6000,
                actions: [
                    {
                        label: 'Keep Going',
                        action: 'encourage'
                    }
                ]
            };
        }

        return null;
    }

    /**
     * Create final debt payment celebration - the ultimate engagement message
     */
    static createDebtFreeMessage(userData) {
        const totalPaid = userData.totalDebtPaid;
        const interestSaved = userData.interestSaved;
        const monthsEarly = userData.monthsEarly;

        return {
            type: 'ultimate_celebration',
            priority: 'ultimate',
            category: 'debt_free',
            title: '🏆 DEBT FREE! 🏆',
            message: `CONGRATULATIONS! You are officially DEBT FREE! You paid off ${formatCurrency(totalPaid)} and saved ${formatCurrency(interestSaved)} in interest!`,
            data: userData,
            displayTime: 15000, // Long display for ultimate achievement
            actions: [
                {
                    label: 'Share Your Victory',
                    action: 'share',
                    data: userData
                },
                {
                    label: 'Plan Your Next Goal',
                    action: 'navigate',
                    target: '/tools'
                }
            ],
            confetti: true // Special visual effect
        };
    }

    /**
     * Generate motivational messages for users who might be struggling
     */
    static generateEncouragementMessage(userState) {
        if (userState.strugglingIndicators?.length > 0) {
            const encouragements = [
                "💪 Small steps count! Every payment brings you closer to freedom.",
                "🌟 You've got this! Progress isn't always linear, but it's always valuable.",
                "🎯 Remember why you started - financial freedom is worth the effort!",
                "🔥 Tough times don't last, but tough people do. Keep going!"
            ];

            return {
                type: 'encouragement',
                priority: 'medium',
                category: 'motivation',
                title: 'Keep Going! 💪',
                message: encouragements[Math.floor(Math.random() * encouragements.length)],
                data: userState,
                displayTime: 8000,
                actions: [
                    {
                        label: 'Adjust Plan',
                        action: 'navigate',
                        target: '/tools/debt-snowball'
                    }
                ]
            };
        }

        return null;
    }
}

export default EngagementMessageService;